import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EventTrackingService } from '@core/services/event-tracking.service';
import { EKpiGoalsStatusBadge } from '@cta-analytics-dialog/cta-analytics-dialog-content/cta-analytics-table/cta-analytics-details/cta-analytics-details-kpi-goals-table/cta-analytics-details-kpi-goals-table.interface';
import { EAnalyticsStatus } from '@enums/campaign-analytics.enum';
import { EEventType, EEventViewMode } from '@enums/event-tracking.enum';
import { IAnalyticsNode, TExpectedKpi } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FilteredCreatorBadgeComponent } from '@shared/components/filtered-creator-badge/filtered-creator-badge.component';
import { toEventFrontendTab } from '@utilities/event-payload-mapping.util';
import { map, startWith } from 'rxjs';
import {
  IDeepLinkCreatorInfo,
  IDeepLinkKpiGoalsFilter,
} from '../campaign-details-deep-link/campaign-details-deep-link.interface';
import { CampaignDetailsDeliverablesFilterSelectComponent } from '../campaign-details-deliverables/campaign-details-deliverables-filter-select/campaign-details-deliverables-filter-select.component';
import { CampaignDetailsDeliverablesGroupToggleComponent } from '../campaign-details-deliverables/campaign-details-deliverables-group-toggle/campaign-details-deliverables-group-toggle.component';
import { CampaignDetailsDeliverablesStatusFiltersComponent } from '../campaign-details-deliverables/campaign-details-deliverables-status-filters/campaign-details-deliverables-status-filters.component';
import { ECampaignDetailsTab, IAnalyticsFilterChangedEvent } from '../campaign-details.interface';
import { ICampaignExportConfig } from '../campaign-export-config/campaign-export-config.interface';
import { CampaignDetailsKpiGoalsTableComponent } from './campaign-details-kpi-goals-table/campaign-details-kpi-goals-table.component';
import {
  ICampaignDetailsKpiGoalsFilterState,
  ICampaignDetailsKpiGoalsGroupedParentRow,
  ICampaignDetailsKpiGoalsTableRow,
  TCampaignDetailsKpiGoalsGroupedRow,
} from './campaign-details-kpi-goals.interface';
import { CampaignDetailsKpiGoalsService } from './campaign-details-kpi-goals.service';

@Component({
  imports: [
    CampaignDetailsDeliverablesFilterSelectComponent,
    CampaignDetailsDeliverablesGroupToggleComponent,
    CampaignDetailsDeliverablesStatusFiltersComponent,
    CampaignDetailsKpiGoalsTableComponent,
    FilteredCreatorBadgeComponent,
    TranslateModule,
  ],
  providers: [CampaignDetailsKpiGoalsService],
  selector: 'app-campaign-details-kpi-goals',
  templateUrl: './campaign-details-kpi-goals.component.html',
})
export class CampaignDetailsKpiGoalsComponent {
  private readonly kpiGoalsService = inject(CampaignDetailsKpiGoalsService);
  private readonly translate = inject(TranslateService);
  private readonly eventTrackingService = inject(EventTrackingService);

  public readonly analyticsNodes = input.required<IAnalyticsNode[] | null>();
  public readonly campaignExportConfig = input.required<ICampaignExportConfig>();
  public readonly loading = input<boolean>(true);
  public readonly deepLinkFilters = input<IDeepLinkKpiGoalsFilter | null>(null);
  public readonly filteredCreator = input<IDeepLinkCreatorInfo | null>(null);

  public readonly kpiGoalsRowClicked = output<ICampaignDetailsKpiGoalsTableRow>();
  public readonly kpiGoalsRowGroupClicked = output<ICampaignDetailsKpiGoalsGroupedParentRow>();
  public readonly filteredCreatorRemoved = output<void>();
  public readonly analyticsFilterChanged = output<IAnalyticsFilterChangedEvent>();

  public readonly filterState = signal<ICampaignDetailsKpiGoalsFilterState>(
    this.kpiGoalsService.createInitialFilterState(),
  );
  public readonly groupByContent = signal<boolean>(false);

  constructor() {
    this.createDeepLinkFiltersEffect();
  }

  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  private readonly effectiveAnalyticsNodes = computed(() => {
    const nodes = this.analyticsNodes();
    const creator = this.filteredCreator();

    if (!creator || !nodes) {
      return nodes;
    }

    return nodes.filter((node) => node.creatorId === creator.id);
  });

  public readonly rows = computed((): ICampaignDetailsKpiGoalsTableRow[] => {
    this.languageChange();

    return this.kpiGoalsService.buildRows(this.effectiveAnalyticsNodes(), this.translate);
  });

  public readonly filterOptions = computed(() => {
    this.languageChange();

    return this.kpiGoalsService.buildFilterOptions(this.rows(), this.translate);
  });

  public readonly ctaTemplateEmptyLabel = computed((): string => {
    this.languageChange();

    return this.translate.instant('pages.campaignDetails.kpiGoals.filters.allDeliverableGroups', {
      number: this.filterOptions().ctaTemplateNames.length,
    }) as string;
  });

  public readonly kpiTypeEmptyLabel = computed((): string => {
    this.languageChange();

    return this.translate.instant('pages.campaignDetails.kpiGoals.filters.allKpiTypes', {
      number: this.filterOptions().kpiTypes.length,
    }) as string;
  });

  public readonly isContentLoading = computed((): boolean => {
    const analyticsNodes = this.analyticsNodes();

    return this.loading() || analyticsNodes === null;
  });

  public readonly statusOptions = computed(() =>
    this.kpiGoalsService.buildStatusOptions(this.filteredRowsForStatusCounting(), this.filterState().statuses),
  );

  /** Maps the API EAnalyticsStatus values (stored in allowedKpiStatuses) to EKpiGoalsStatusBadge keys. */
  private static readonly KPI_API_TO_BADGE: Partial<Record<EAnalyticsStatus, EKpiGoalsStatusBadge>> = {
    [EAnalyticsStatus.failed]: EKpiGoalsStatusBadge.kpiGoalsUnavailable,
    [EAnalyticsStatus.problem]: EKpiGoalsStatusBadge.kpiGoalsBelowTarget,
    [EAnalyticsStatus.ok]: EKpiGoalsStatusBadge.kpiGoalsReached,
  };

  public readonly visibleStatusOptions = computed(() => {
    const allowed = this.campaignExportConfig().allowedKpiStatuses;
    const options = this.statusOptions();

    if (allowed === null) {
      return options;
    }

    const allowedBadges = new Set(
      allowed
        .map((s) => CampaignDetailsKpiGoalsComponent.KPI_API_TO_BADGE[s])
        .filter((b): b is EKpiGoalsStatusBadge => b !== undefined),
    );

    return options.filter((o) => allowedBadges.has(o.key as EKpiGoalsStatusBadge));
  });

  private readonly filteredRowsForStatusCounting = computed(() => {
    const statusIgnoredState: ICampaignDetailsKpiGoalsFilterState = {
      ...this.filterState(),
      statuses: new Set<EKpiGoalsStatusBadge>(),
    };

    return this.kpiGoalsService.filterRows(this.rows(), statusIgnoredState);
  });

  public readonly filteredRows = computed(() => this.kpiGoalsService.filterRows(this.rows(), this.filterState()));

  public readonly groupedRows = computed<TCampaignDetailsKpiGoalsGroupedRow[]>(() =>
    this.kpiGoalsService.buildGroupedRows(this.filteredRows()),
  );

  public onCtaTemplateNamesChanged(values: Set<string>): void {
    const previousValues = this.filterState().ctaTemplateNames;

    this.filterState.update((currentState) => ({
      ...currentState,
      ctaTemplateNames: values,
    }));

    this.emitFilterChanged('cta_template_names', previousValues, values);
  }

  public onKpiTypesChanged(values: Set<string>): void {
    const previousValues = this.filterState().kpiTypes;

    this.filterState.update((currentState) => ({
      ...currentState,
      kpiTypes: new Set(Array.from(values).map((value) => value as TExpectedKpi)),
    }));

    this.emitFilterChanged('kpi_types', previousValues, values);
  }

  public onStatusesChanged(values: Set<EKpiGoalsStatusBadge>): void {
    const previousValues = this.filterState().statuses;

    this.filterState.update((currentState) => ({
      ...currentState,
      statuses: values,
    }));

    this.emitFilterChanged('statuses', previousValues, values);
  }

  public onGroupByContentChanged(value: boolean): void {
    this.groupByContent.set(value);

    this.eventTrackingService.track(EEventType.campaignAnalyticsGroupByContentChanged, {
      currentTab: toEventFrontendTab(ECampaignDetailsTab.kpiGoals),
      newValue: String(value),
      previousValue: String(!value),
      viewMode: value ? EEventViewMode.groupedByContent : EEventViewMode.list,
    });
  }

  public onKpiGoalsRowClicked(row: ICampaignDetailsKpiGoalsTableRow): void {
    this.kpiGoalsRowClicked.emit(row);
  }

  public onKpiGoalsRowGroupClicked(row: ICampaignDetailsKpiGoalsGroupedParentRow): void {
    this.kpiGoalsRowGroupClicked.emit(row);
  }

  private emitFilterChanged(currentFilter: string, previousValues: Set<unknown>, currentValues: Set<unknown>): void {
    this.analyticsFilterChanged.emit({
      currentFilter,
      currentFilterValue: Array.from(currentValues).join(','),
      previousFilterValue: Array.from(previousValues).join(','),
    });
  }

  private createDeepLinkFiltersEffect(): void {
    effect(() => {
      const filters = this.deepLinkFilters();

      if (!filters) {
        return;
      }

      untracked(() => {
        this.filterState.set({
          ...this.kpiGoalsService.createInitialFilterState(),
          statuses: filters.statuses ?? new Set(),
        });
      });
    });
  }
}
